/* eslint-disable */
import { useMemo } from 'react';
import { isDevDone, isDevStopped, devHasNoImage, devIsImported } from '../../../utils/devStatusFilter';
import { devIsMovedToOrder, LOCK_KEY } from '../../../utils/devLock';

// ============================================================
// useDevStageCounts — the Convention #61 hybrid, as DevsBoardSummary does it:
// no non-status filter → the cached get_dev_stage_counts envelope; any
// non-status filter (search, designer, factory, material, shape, season,
// sunOptic, decision, procurement, type) → a client-side reduce over
// filteredIgnoringStatusList. Season is included — the panel's gate omits it
// (discovery §2, bug candidate) and this bar must not inherit that.
// ============================================================

export const STAGE_DEFS = [
  { key: 'Drawing stage',          name: 'Drawing',        color: 'var(--st-draw)' },
  { key: 'Hand made sample stage', name: 'Hand made',      color: 'var(--st-hand)' },
  { key: 'Color sample stage',     name: 'Colour sample',  color: 'var(--st-color)' },
  { key: '_done',                  name: 'Completed',      color: 'var(--st-done)' },
  { key: LOCK_KEY,                 name: 'Moved to order', color: 'var(--st-lock)' },
  { key: '_stopped',               name: 'Stopped',        color: 'var(--st-stop)' },
];
export const EXTRA_DEFS = [
  { key: '_no_image', name: 'No image', color: 'var(--st-noimg)' },
  { key: '_imported', name: 'Imported', color: 'var(--st-noimg)' },
];
export const STAGE_LABEL = Object.fromEntries([...STAGE_DEFS, ...EXTRA_DEFS].map((s) => [s.key, s.name]));

const NON_STATUS_AXES = ['designer', 'type', 'material', 'shape', 'season', 'factory', 'sunOptic', 'decision', 'procurement'];

export function hasNonStatusFilter(devFilters, devSearch) {
  if (devSearch && devSearch.length > 0) return true;
  return NON_STATUS_AXES.some((k) => devFilters?.[k]?.size > 0);
}

export function useDevStageCounts({ counts, devFilters, devSearch, filteredDevsList, filteredIgnoringStatusList }) {
  const filtered = hasNonStatusFilter(devFilters, devSearch);
  return useMemo(() => {
    if (!filtered) {
      const c = counts || {};
      return {
        ready: !!counts,
        filtered: false,
        total: c.total ?? 0,
        'Drawing stage': c['Drawing stage'] ?? 0,
        'Hand made sample stage': c['Hand made sample stage'] ?? 0,
        'Color sample stage': c['Color sample stage'] ?? 0,
        [LOCK_KEY]: c._moved_to_order ?? 0,
        _done: c._done ?? 0, _stopped: c._stopped ?? 0, _no_image: c._no_image ?? 0, _imported: c._imported ?? 0,
      };
    }
    const base = filteredIgnoringStatusList || [];
    let draw = 0, hand = 0, color = 0, done = 0, stopped = 0, noImg = 0, imp = 0, moved = 0;
    for (const d of base) {
      if (devIsMovedToOrder(d)) { moved++; continue; }
      if (isDevStopped(d)) { stopped++; continue; }
      if (isDevDone(d)) { done++; continue; }
      const v = (d.stage_status || d.data?.stage_status || '').trim();
      if (v === 'Drawing stage') draw++;
      else if (v === 'Hand made sample stage') hand++;
      else if (v === 'Color sample stage') color++;
    }
    for (const d of base) { if (devHasNoImage(d)) noImg++; if (devIsImported(d)) imp++; }
    return {
      ready: true, filtered: true,
      total: filteredDevsList?.length ?? 0, scopeTotal: base.length,
      'Drawing stage': draw, 'Hand made sample stage': hand, 'Color sample stage': color,
      [LOCK_KEY]: moved, _done: done, _stopped: stopped, _no_image: noImg, _imported: imp,
    };
  }, [filtered, counts, filteredDevsList, filteredIgnoringStatusList]);
}
